import React, { FC } from 'react'
import { SideDrawer, SideDrawerForm } from '@sales-center/components'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import { useGetRecordDetailTitles } from '@user/features/GlobalSearch/FilterDrawer/hooks/useGetRecordDetailTitles'
import { useRecordDetails } from '@user/features/GlobalSearch/FilterDrawer/hooks/useGetRecordDetails'
import Checkbox from '@mui/material/Checkbox'
import { useSaveCustomizedResultsSettings } from '@user/store/globalSearch/hooks/useSaveCustomizeResultsSettings'
import { Theme } from '@mui/material/styles'

interface Props {
  onClose: () => void
  open: boolean
}

const fontColor = { color: '#000000DE' }
const div = { width: '18px', height: '2px', backgroundColor: '#00000099' }

export const FilterDrawer: FC<Props> = ({ onClose, open }) => {
  const { titles } = useGetRecordDetailTitles()
  const { recordDetails, onChangeCheckBox, recordStats } = useRecordDetails()

  const onSubmit = useSaveCustomizedResultsSettings({ recordDetails, onClose })

  return (
    <SideDrawer onClose={onClose} open={open} width={550}>
      <SideDrawerForm
        disableSave={false}
        onCancel={onClose}
        onSave={onSubmit}
        primaryBtnLabel="Save"
        containerTop={'0px'}
      >
        <Box p={2}>
          <Typography variant="h6">Customize Results Display</Typography>
          <Typography
            variant="subtitle1"
            sx={{
              ...fontColor,
              lineHeight: '24px',
              marginTop: (theme: Theme) => theme.spacing(1.6),
              marginBottom: (theme: Theme) => theme.spacing(4),
            }}
          >
            Choose up to three additional record details to be displayed in the
            results. Fields not selected will appear in the additional
            information area.
          </Typography>

          <Grid container>
            <Grid item xs={12} md={4}>
              <Typography variant="body1" sx={fontColor}>
                Record Details
              </Typography>
            </Grid>
            <Grid item xs={3} display={{ xs: 'block', md: 'none' }} />

            {titles.map((title, index) => (
              <Grid
                key={`record-detail-title-${index}`}
                item
                xs={title.col[0]}
                md={title.col[1]}
              >
                <Grid
                  container
                  display={'flex'}
                  alignItems={'center'}
                  direction={'column'}
                >
                  <Typography variant="caption" sx={fontColor}>
                    {title.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      ...fontColor,
                      marginTop: (theme: Theme) => theme.spacing(1.2),
                      marginBottom: (theme: Theme) => theme.spacing(1.2),
                    }}
                  >
                    {recordStats[title.key]}/3
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>

          {recordDetails.map((item, index) => (
            <Grid
              container
              key={`record-detail-${index}`}
              display={'flex'}
              alignItems={'center'}
              sx={{
                bgcolor:
                  index % 2 != 0
                    ? (theme: Theme) => theme.palette.common.white
                    : '#F3F5F7',
                borderRadius: '4px',
              }}
            >
              <Grid
                item
                xs={3}
                md={4}
                sx={{ paddingLeft: (theme: Theme) => theme.spacing(1) }}
              >
                <Typography variant="subtitle2" sx={fontColor}>
                  {item.title}
                </Typography>
              </Grid>

              {item.checkBoxes.map((box, boxIndex) => (
                <Grid
                  key={`record-detail-checkbox-${boxIndex}`}
                  item
                  xs={3}
                  md={boxIndex === 1 ? 2 : 3}
                  display={'flex'}
                  justifyContent={'center'}
                >
                  {box.available ? (
                    <Checkbox
                      sx={{ color: '#00000099' }}
                      name={item.title.replaceAll(' ', '_').toLowerCase()}
                      onChange={() => onChangeCheckBox(index, boxIndex)}
                      checked={box.checked}
                      disabled={recordStats[box.key] === 3 && !box.checked}
                    />
                  ) : (
                    <div style={div} />
                  )}
                </Grid>
              ))}
            </Grid>
          ))}
        </Box>
      </SideDrawerForm>
    </SideDrawer>
  )
}

FilterDrawer.displayName = 'FilterDrawer'
